import OpenButton from "@/components/toto/OpenButton";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, boutiqueList } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UnderstandingReview } from "@/components/understanding/UnderstandingReview";
import "@/components/toto/PayrollWorkspace.css";
import "@/components/toto/PayrollWorkspaceOperational.css";

const ACTIVE_PERIOD = "BW-2026-08-21";
const ACTIVE_WORK_DATES = "Aug 3–16, 2026";
const ACTIVE_PAY_DATE = "Aug 21, 2026";
const LAST_COMPLETED_PERIOD = "BW-2026-08-07";
const CYCLE = "biweekly";
const PAYROLL_BUILD = "PAYROLL WORKBENCH · 2026.08.12-C";
const ZENOTI_WORKSPACE_URL = "/settings/connections/zenoti";

const ZENOTI_RELATIONSHIPS = [
  ["Time records", "Worked-time verification and weekend eligibility"],
  ["Sales & Services", "Enhancements, requests and service-linked payroll evidence"],
  ["Membership Sales", "EG membership commission evidence"],
  ["Memberships Payment", "First recurring collection evidence"],
];

const BOUTIQUE_LABELS = {
  PLT: "Plantation",
  FLL: "Blue Massage of Fort Lauderdale",
};

function canonicalBoutiqueCode(value) {
  const raw = String(value || "").trim();
  if (!raw) return "PLT";
  const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const match = boutiqueList().find((item) => [
    item.code,
    item.name,
    item.shortName,
    item.presenceTint,
  ].some((identity) => String(identity || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim() === normalized));
  return match?.code || raw.toUpperCase();
}

function PayrollUnderstandingCore() {
  return (
    <span className="toto-payroll-vessel-core" aria-hidden="true">
      <span className="toto-payroll-relation toto-payroll-relation-a"><i /></span>
      <span className="toto-payroll-relation toto-payroll-relation-b"><i /></span>
      <span className="toto-payroll-relation toto-payroll-relation-c"><i /></span>
      <span className="toto-payroll-relation toto-payroll-relation-d"><i /></span>
      <span className="toto-payroll-relation toto-payroll-relation-e"><i /></span>
      <span className="toto-payroll-understanding toto-payroll-understanding-center" />
      <span className="toto-payroll-understanding toto-payroll-understanding-adp" />
      <span className="toto-payroll-understanding toto-payroll-understanding-evidence" />
      <span className="toto-payroll-understanding toto-payroll-understanding-calculators" />
      <span className="toto-payroll-understanding toto-payroll-understanding-rules" />
      <span className="toto-payroll-understanding toto-payroll-understanding-history" />
      <span className="toto-payroll-breath" />
    </span>
  );
}

function receiptLabel(receipt) {
  const state = receipt?.receipt_state;
  if (state === "understood") return "Understood";
  if (state === "not_understood") return "Needs your eyes";
  if (state === "processing") return "Reading";
  return "Received";
}

export default function PayrollWorkspace({ open, onOpen, onClose, designPreviewBoutique = null }) {
  const { user } = useAuth();
  const liveBoutique = canonicalBoutiqueCode(user?.boutique_code);
  const boutique = designPreviewBoutique || liveBoutique;
  const boutiqueLabel = BOUTIQUE_LABELS[boutique] || boutique;
  const payrollCalculatorHub = `/payroll/prepare?${new URLSearchParams({ boutique: liveBoutique, period: ACTIVE_PERIOD, cycle: CYCLE }).toString()}`;
  const inputRef = useRef(null);
  const [preparation, setPreparation] = useState(null);
  const [context, setContext] = useState(null);
  const [acceptedInputs, setAcceptedInputs] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [pendingUnderstandings, setPendingUnderstandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [zenotiMessage, setZenotiMessage] = useState(null);

  const loadPayroll = useCallback(async () => {
    if (designPreviewBoutique) {
      setPreparation(null);
      setContext(null);
      setAcceptedInputs(null);
      setReceipts([]);
      setPendingUnderstandings([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [stateResult, contextResult, acceptedResult] = await Promise.allSettled([
        api.get(`/payroll/prepare/${liveBoutique}/${ACTIVE_PERIOD}?cycle=${CYCLE}`),
        api.get(`/payroll/prepare/${liveBoutique}/${ACTIVE_PERIOD}/context?cycle=${CYCLE}`),
        api.get("/payroll/prepare/accepted-inputs"),
      ]);
      setPreparation(stateResult.status === "fulfilled" ? stateResult.value.data || null : null);
      setContext(contextResult.status === "fulfilled" ? contextResult.value.data || null : null);
      setAcceptedInputs(acceptedResult.status === "fulfilled" ? acceptedResult.value.data?.accepted_inputs || null : null);

      const payrollFailure = [stateResult, contextResult].find((result) => result.status === "rejected");
      if (payrollFailure) throw payrollFailure.reason;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Payroll understanding is temporarily unavailable to this preview.");
    } finally {
      setLoading(false);
    }
  }, [designPreviewBoutique, liveBoutique]);

  const loadPendingUnderstandings = useCallback(async () => {
    if (designPreviewBoutique) {
      setPendingUnderstandings([]);
      return;
    }
    try {
      // Keep the review queue on the same scoped payroll surface as the
      // evidence intake. The generic Understanding endpoint has a stricter
      // authorization path and may reject an otherwise authorized payroll
      // manager, which previously hid arrived reports after a refresh.
      const res = await api.get(
        `/payroll/prepare/${liveBoutique}/${ACTIVE_PERIOD}/understandings`,
        { params: { cycle: CYCLE, limit: 20 } },
      );
      setPendingUnderstandings(res.data?.understandings || []);
    } catch {
      // Evidence remains safely stored even if its review surface is
      // temporarily unavailable. Payroll itself must remain usable.
      setPendingUnderstandings([]);
    }
  }, [designPreviewBoutique, liveBoutique]);

  useEffect(() => {
    if (!open) return;
    loadPayroll();
    loadPendingUnderstandings();
  }, [open, loadPayroll, loadPendingUnderstandings]);

  const onUnderstandingResolved = async (understanding) => {
    setPendingUnderstandings((current) =>
      current.map((item) => item.id === understanding.id ? understanding : item)
    );
    await Promise.all([loadPayroll(), loadPendingUnderstandings()]);
  };

  const reports = preparation?.reports_needed || [];
  const expectedReportPlan = preparation?.expected_report_plan;
  const reportPlanAwaitingConfirmation = expectedReportPlan?.status === "AWAITING_CONFIRMATION";
  const calculations = preparation?.calculations || [];
  const reportsReceived = reports.filter((r) => r.uploaded).length;
  const calculationsResolved = calculations.filter((c) => ["ready", "exception", "not_included"].includes(c.state)).length;
  const concerns = context?.unresolved_concerns || [];
  const readyForExport = Boolean(preparation?.ready_for_export);
  const exported = Boolean(preparation?.exported_at);
  const timeVerified = Boolean(preparation?.time_records_verified);

  const accept = useMemo(() => {
    if (!acceptedInputs) return ".csv,.pdf,.xlsx,.xls";
    const exts = new Set();
    Object.values(acceptedInputs).forEach((entry) => {
      (entry?.extensions || []).forEach((ext) => exts.add(ext.startsWith(".") ? ext : `.${ext}`));
    });
    return Array.from(exts).join(",") || ".csv,.pdf,.xlsx,.xls";
  }, [acceptedInputs]);

  const handleFiles = async (event) => {
    if (designPreviewBoutique) return;
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setUploadMessage(`Reading ${files.length} ${files.length === 1 ? "file" : "files"}…`);
    try {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      const res = await api.post(`/payroll/prepare/${liveBoutique}/${ACTIVE_PERIOD}/inbox?cycle=${CYCLE}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      const nextReceipts = res.data?.receipts || [];
      setReceipts(nextReceipts);
      const needsEyes = nextReceipts.filter((r) => r.receipt_state === "not_understood" || r.classification?.confirmed === false).length;
      setUploadMessage(needsEyes ? `${files.length} received · ${needsEyes} need your eyes` : `${files.length} received and routed`);
      await Promise.all([loadPayroll(), loadPendingUnderstandings()]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setUploadMessage(typeof detail === "string" ? detail : detail?.message || "The evidence was not accepted. Nothing was silently discarded.");
    } finally {
      setUploading(false);
    }
  };

  const confirmExpectedReports = async () => {
    if (designPreviewBoutique || !expectedReportPlan) return;
    setUploading(true);
    try {
      await api.post(
        `/payroll/prepare/${liveBoutique}/${ACTIVE_PERIOD}/report-plan/confirm?cycle=${CYCLE}`,
        { report_kinds: expectedReportPlan.reports.map((report) => report.kind) },
      );
      await loadPayroll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setUploadMessage(typeof detail === "string" ? detail : detail?.message || "The report plan changed. Refresh and review it again.");
    } finally {
      setUploading(false);
    }
  };

  const openZenoti = () => {
    const opened = window.open(ZENOTI_WORKSPACE_URL, "_blank", "noopener,noreferrer");
    setZenotiMessage(opened
      ? "SONMI's protected Zenoti workspace opened in a separate tab. Exported reports return here through Add Evidence."
      : "The browser blocked the protected Zenoti workspace. Allow pop-ups for SONMI and try again.");
  };

  return (
    <div className={`toto-payroll-workspace ${open ? "is-slide-open" : "is-vessel"} ${designPreviewBoutique ? "is-design-preview" : ""}`}>
      <button type="button" className="toto-payroll-vessel" onClick={onOpen} aria-label="Bring Payroll onto a Slide" aria-expanded={open}>
        <PayrollUnderstandingCore />
        <span className="toto-payroll-vessel-halo" aria-hidden="true" />
        <span className="toto-payroll-vessel-label">Payroll</span>
      </button>
      <div className="toto-payroll-tether" aria-hidden="true" />
      <section className="toto-payroll-slide" aria-hidden={!open} aria-label="Payroll Slide">
        <div className="toto-payroll-slide-surface" />
        <header className="toto-payroll-header">
          <div>
            <h2>Payroll</h2>
            <p className="toto-payroll-subtitle">The current run is one view into a larger understanding of people, evidence, rules, calculations and handoff.</p>
            <p style={{ margin: "7px 0 0", fontSize: "8px", letterSpacing: ".12em", color: "rgba(55,61,56,.34)" }}>{PAYROLL_BUILD}</p>
          </div>
          <div className="toto-payroll-header-actions">
            <Link className="toto-payroll-calculator-link" to={payrollCalculatorHub}>Payroll calculators</Link>
            <button type="button" className="toto-payroll-release" onClick={onClose}>Release</button>
          </div>
        </header>

        <div className="toto-payroll-scroll">
          {designPreviewBoutique && (
            <div className="toto-payroll-message" style={{ margin: "10px 0 18px" }}>
              Team Blue design preview · Fort Lauderdale payroll data and actions remain protected.
            </div>
          )}
          {loading && <div className="toto-payroll-message" style={{ margin: "10px 0 18px" }}>Reading live payroll data…</div>}
          {error && <div className="toto-payroll-message is-error" style={{ margin: "10px 0 18px" }}>{error} The workbench remains available while the connection is restored.</div>}

          <section className="toto-payroll-current" aria-label="Current payroll">
            <div className="toto-payroll-current-title">
              <p className="toto-payroll-eyebrow">Current payroll</p>
              <h3>{ACTIVE_WORK_DATES}</h3>
              <p>{boutiqueLabel} · Pay date {ACTIVE_PAY_DATE}</p>
              <span className={`toto-payroll-run-state ${concerns.length ? "needs-eyes" : ""}`}>
                {exported ? "Handed off" : readyForExport ? "Ready for handoff" : preparation ? "In progress" : "Ready to begin"}
              </span>
            </div>
            <div className="toto-payroll-stats" aria-label="Payroll status">
              <article><span>Evidence</span><strong>{preparation ? `${reportsReceived}/${reports.length || "—"}` : "—"}</strong></article>
              <article><span>Calculations</span><strong>{preparation ? `${calculationsResolved}/${calculations.length || "—"}` : "—"}</strong></article>
              <article className={concerns.length ? "needs-eyes" : ""}><span>Attention</span><strong>{preparation ? concerns.length : "—"}</strong></article>
            </div>
            <div className="toto-payroll-flow" aria-label="Payroll workflow">
              <div><i className={reportsReceived ? "is-known" : ""} /><span>Evidence</span></div>
              <div><i className={reportsReceived ? "is-known" : ""} /><span>Understand</span></div>
              <div><i className={concerns.length ? "is-attention" : calculationsResolved ? "is-known" : ""} /><span>Review</span></div>
              <div><i className={exported ? "is-known" : readyForExport ? "is-ready" : ""} /><span>Handoff</span></div>
            </div>
          </section>

          <section className="toto-payroll-workbench">
            {reportPlanAwaitingConfirmation && (
              <article className="toto-payroll-workcard toto-payroll-evidence-card" aria-label="Confirm expected payroll reports">
                <div className="toto-payroll-workcard-head">
                  <div><p className="toto-payroll-eyebrow">Before calculations</p><h3>These are the reports needed</h3></div>
                  <button type="button" className="toto-payroll-primary" onClick={confirmExpectedReports} disabled={uploading || Boolean(designPreviewBoutique)}>
                    Confirm reports
                  </button>
                </div>
                <p className="toto-payroll-workcard-copy">SONMI selected this concise set from the payroll period. Confirm it once; calculator results remain quiet until then.</p>
                <div className="toto-payroll-evidence-grid">
                  {(expectedReportPlan.reports || []).map((report) => <div className="toto-payroll-evidence-item is-missing" key={report.kind}><i /><div><strong>{report.label}</strong><small>{report.source}</small></div><span>Expected</span></div>)}
                </div>
              </article>
            )}
            <article className="toto-payroll-workcard toto-payroll-evidence-card">
              <div className="toto-payroll-workcard-head">
                <div><p className="toto-payroll-eyebrow">Next step</p><h3>Add payroll evidence</h3></div>
                <button type="button" className="toto-payroll-primary" onClick={() => inputRef.current?.click()} disabled={uploading || reportPlanAwaitingConfirmation || Boolean(designPreviewBoutique)}>{uploading ? "Reading…" : "Add Evidence"}</button>
                <input ref={inputRef} className="toto-payroll-file-input" type="file" multiple accept={accept} onChange={handleFiles} />
              </div>
              <p className="toto-payroll-workcard-copy">{reportPlanAwaitingConfirmation ? "Confirm the expected reports above before evidence is collected." : "Drop in the reports you have. TOTO reads, routes, and keeps only the decisions that need your attention visible."}</p>
              {uploadMessage && <div className="toto-payroll-receipt-note">{uploadMessage}</div>}
              {reports.length ? <div className="toto-payroll-evidence-grid">{reports.map((report) => <div key={report.kind} className={`toto-payroll-evidence-item ${report.uploaded ? "is-present" : "is-missing"}`}><i /><div><strong>{report.label}</strong><small>{report.source}</small></div><span>{report.uploaded ? "Present" : "Awaiting"}</span></div>)}</div> : <div className="toto-payroll-receipt-note">Live preparation data is not loaded yet. Add Evidence remains available.</div>}
              {!!receipts.length && <div className="toto-payroll-receipts">{receipts.map((receipt) => <div key={receipt.artifact_id || receipt.filename} className={`toto-payroll-receipt is-${receipt.receipt_state || "received"}`}><div><strong>{receipt.filename || "Evidence"}</strong><small>{receipt.report_kind || receipt.classification?.proposed_report_kind || "Classifying"}</small></div><span>{receiptLabel(receipt)}</span></div>)}</div>}
            </article>

            {pendingUnderstandings.map((understanding) => (
              <div className="toto-payroll-understanding-review" key={understanding.id}>
                <UnderstandingReview
                  understanding={understanding}
                  onResolved={onUnderstandingResolved}
                  onDismiss={() => setPendingUnderstandings((current) => current.filter((item) => item.id !== understanding.id))}
                  testId={`payroll-understanding-${understanding.id}`}
                />
              </div>
            ))}

            <div className="toto-payroll-details">
              <details className="toto-payroll-detail" open={Boolean(concerns.length)}>
                <summary><span><p className="toto-payroll-eyebrow">Attention</p><strong>{concerns.length ? `${concerns.length} item${concerns.length === 1 ? "" : "s"} need your eyes` : "Nothing needs you right now"}</strong></span><OpenButton as="span" aria-hidden="true"/></summary>
                {concerns.length ? <div className="toto-payroll-concern-list">{concerns.map((concern, index) => <div key={`${concern.kind}-${index}`}><strong>{concern.calculator || "Payroll"}</strong><p>{concern.detail}</p></div>)}</div> : <p className="toto-payroll-workcard-copy">TOTO will bring attribution gaps, missing evidence, and exceptions here. Routine proof stays quiet.</p>}
              </details>

              <details className="toto-payroll-detail">
                <summary><span><p className="toto-payroll-eyebrow">Calculators</p><strong>{preparation ? `${calculationsResolved} of ${calculations.length || 0} ready` : "Open the calculator workspace"}</strong></span><OpenButton as="span" aria-hidden="true"/></summary>
                <div className="toto-payroll-detail-action"><p className="toto-payroll-workcard-copy">Open a calculator to review its evidence, calculation, exceptions, and approval separately from this current-run view.</p><OpenButton to={payrollCalculatorHub} aria-label="Open calculators" /></div>
                {calculations.length ? <div className="toto-payroll-calculation-list">{calculations.map((calc) => <div key={calc.key} className={`toto-payroll-calc-row is-${calc.state}`}><i /><div><strong>{calc.label}</strong><small>{calc.consumed_reports?.length ? calc.consumed_reports.join(" · ").replaceAll("_", " ") : calc.not_included_reason || "Persistent payroll relationship"}</small></div><span>{calc.state === "ready" ? "Ready" : calc.state === "exception" ? "Review" : calc.state === "not_included" ? "Not in this run" : "Waiting"}</span></div>)}</div> : <p className="toto-payroll-workcard-copy">Calculator status will appear once payroll evidence has been read.</p>}
              </details>

              <details className="toto-payroll-detail toto-payroll-zenoti-detail">
                <summary><span><p className="toto-payroll-eyebrow">Source</p><strong>Zenoti workspace</strong></span><OpenButton as="span" aria-hidden="true"/></summary>
                <div className="toto-payroll-detail-action"><p className="toto-payroll-workcard-copy">Use Zenoti to export source reports, then return here to add them as payroll evidence.</p><OpenButton onClick={openZenoti} disabled={Boolean(designPreviewBoutique)} aria-label="Open Zenoti"/></div>
                {zenotiMessage && <div className="toto-payroll-receipt-note">{zenotiMessage}</div>}
                <div className="toto-payroll-calculation-list">{ZENOTI_RELATIONSHIPS.map(([label, role]) => <div key={label} className="toto-payroll-calc-row is-pending"><i /><div><strong>{label}</strong><small>{role}</small></div><span>Zenoti</span></div>)}</div>
              </details>
            </div>
          </section>
          <footer className="toto-payroll-footer"><span>{preparation ? "Live SONMI payroll state" : "Payroll workbench · connection independent"}</span><span>{boutique} · {ACTIVE_PERIOD}</span></footer>
        </div>
      </section>
    </div>
  );
}
